import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import cloudApi from '../services/cloudApi';

const SESSION_DISMISS_KEY = 'tinv_dismissed';
const FROM_INVITATION_KEY = 'fromInvitation';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const ModalShell = ({ children, onBackdropClick }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onBackdropClick} />
      <div
        className={`relative bg-white dark:bg-gray-900/95 rounded-2xl border border-gray-200/80 dark:border-gray-700/50 shadow-2xl shadow-cyan-500/10 dark:shadow-cyan-500/5 w-full max-w-md overflow-hidden transition-all duration-500 ease-out ${show ? 'animate-scale-up' : 'opacity-0 scale-95'}`}
      >
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500" />
        {children}
      </div>
    </div>
  );
};

// Workspace tile — square gradient block with the workspace's first letter
const WorkspaceTile = ({ name }) => (
  <div className="relative w-12 h-12 rounded-xl flex-shrink-0">
    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-500 opacity-20 dark:opacity-25 blur-md" />
    <div className="relative w-12 h-12 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/20">
      <div className="w-full h-full rounded-[10.5px] bg-white dark:bg-gray-900 flex items-center justify-center">
        <span className="text-base font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
          {(name || '?').charAt(0).toUpperCase()}
        </span>
      </div>
    </div>
  </div>
);

// Animated check used for the success state — same drawing style as CheckoutSuccessModal
const SuccessMark = () => (
  <div className="relative mx-auto w-16 h-16 animate-scale-up">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-20 dark:opacity-30 blur-xl" />
    <div className="relative w-16 h-16 rounded-2xl p-[2px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20">
      <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[14px] flex items-center justify-center">
        <svg viewBox="0 0 36 36" className="w-9 h-9">
          <style>{`
            @keyframes invCircleDraw { from { stroke-dashoffset: 88; } to { stroke-dashoffset: 0; } }
            @keyframes invCheckDraw { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
          `}</style>
          <circle cx="18" cy="18" r="14" fill="none" stroke="url(#successGradient)" strokeWidth="2.5" strokeLinecap="round"
            style={{ strokeDasharray: 88, strokeDashoffset: 88, animation: 'invCircleDraw 0.5s ease-out 0.2s forwards' }} />
          <path d="M11 19l4 4 9-9" fill="none" stroke="url(#successGradient)" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"
            style={{ strokeDasharray: 24, strokeDashoffset: 24, animation: 'invCheckDraw 0.35s ease-out 0.65s forwards' }} />
          <defs>
            <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="50%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#c084fc" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  </div>
);

const PrimaryButton = ({ onClick, disabled, children, className = '' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-5 py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 transition-all duration-200 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ onClick, disabled, children, className = '' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

const PendingInvitationsModal = () => {
  const { isAuthenticated, loading, refreshWorkspaces, switchWorkspace } = useAuth();
  const [cameFromEmail] = useState(() => sessionStorage.getItem(FROM_INVITATION_KEY) === '1');

  const [invitations, setInvitations] = useState([]);
  const [open, setOpen] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (!cameFromEmail && sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') return;

    let cancelled = false;
    (async () => {
      try {
        const list = await cloudApi.getPendingInvitations();
        if (cancelled) return;
        if (list.length > 0) {
          setInvitations(list);
          setOpen(true);
        } else if (cameFromEmail) {
          setOpen(true);
        }
      } catch (err) {
        console.warn('Failed to load pending invitations:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, loading, cameFromEmail]);

  const clearInvitedFlag = () => {
    sessionStorage.removeItem(FROM_INVITATION_KEY);
  };

  const handleAccept = async (invitation) => {
    setActionId(invitation.id);
    setError(null);
    try {
      await cloudApi.acceptInvitationById(invitation.id);
      await refreshWorkspaces();
      // Don't switch yet — wait for the user to dismiss the success card so the
      // "Switched to {workspace}" toast appears right when they click through.
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id));
      setAccepted({ id: invitation.workspace_id, name: invitation.workspace_name });
    } catch (err) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setActionId(null);
    }
  };

  const handleDecline = async (invitation) => {
    setActionId(invitation.id);
    setError(null);
    try {
      await cloudApi.declineInvitationById(invitation.id);
      setInvitations((prev) => {
        const next = prev.filter((inv) => inv.id !== invitation.id);
        if (next.length === 0) {
          setOpen(false);
          clearInvitedFlag();
        }
        return next;
      });
    } catch (err) {
      setError(err.message || 'Failed to decline invitation');
    } finally {
      setActionId(null);
    }
  };

  const handleLater = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    setOpen(false);
    clearInvitedFlag();
  };

  const handleDismissSuccess = async () => {
    // Trigger the workspace switch + confirmation toast at the moment the user
    // clicks "Go to workspace" — feels more like a deliberate action than a
    // background change that happened while they read the success card.
    if (accepted?.id) {
      try {
        await switchWorkspace(accepted.id);
      } catch {
        // AuthContext already picked a sensible workspace; ignore
      }
    }
    setAccepted(null);
    if (invitations.length === 0) {
      setOpen(false);
      clearInvitedFlag();
    }
  };

  if (!open) return null;

  // ===== Success state
  if (accepted) {
    return (
      <ModalShell onBackdropClick={handleDismissSuccess}>
        <div className="px-8 pt-10 pb-4 text-center">
          <SuccessMark />
          <h3 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
            You joined {accepted.name}
          </h3>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            You're now viewing this workspace. Switch back any time from the workspace menu.
          </p>
        </div>
        <div className="px-8 pb-8 pt-2">
          <PrimaryButton onClick={handleDismissSuccess} className="w-full">
            {invitations.length > 0 ? 'Continue' : 'Go to workspace'}
          </PrimaryButton>
        </div>
      </ModalShell>
    );
  }

  // ===== Empty state — user came from email but no pending invite exists
  if (invitations.length === 0 && cameFromEmail) {
    return (
      <ModalShell onBackdropClick={handleLater}>
        <div className="px-8 pt-10 pb-4 text-center">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            No pending invitations
          </h3>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            This invitation may have already been accepted, declined, or expired. If you think this is a mistake, ask the workspace owner to invite you again.
          </p>
        </div>
        <div className="px-8 pb-8 pt-2">
          <PrimaryButton onClick={handleLater} className="w-full">
            Got it
          </PrimaryButton>
        </div>
      </ModalShell>
    );
  }

  if (invitations.length === 0) return null;

  // ===== Main list state
  // Single-invite vs multi-invite get distinct layouts:
  //  - Single: workspace is the visual anchor (big tile + name as headline)
  //  - Multi: compact list with one accept per row
  const single = invitations.length === 1 ? invitations[0] : null;

  return (
    <ModalShell onBackdropClick={handleLater}>
      {single ? (
        <div className="px-8 pt-9 pb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 text-center">
            You're invited to join
          </p>

          <div className="mt-5 flex items-center gap-4">
            <WorkspaceTile name={single.workspace_name} />
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                {single.workspace_name}
              </h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Workspace
              </p>
            </div>
          </div>

          <dl className="mt-6 space-y-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800/70 px-4 py-3">
            <div className="flex items-baseline gap-3 min-w-0">
              <dt className="w-20 flex-shrink-0 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                Invited by
              </dt>
              <dd className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-200 truncate">
                {single.invited_by_name || single.invited_by_email || 'a workspace owner'}
              </dd>
            </div>
            {single.invited_by_email && single.invited_by_name && (
              <div className="flex items-baseline gap-3 min-w-0">
                <dt className="w-20 flex-shrink-0 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Email
                </dt>
                <dd className="flex-1 min-w-0 text-sm text-gray-500 dark:text-gray-400 truncate" title={single.invited_by_email}>
                  {single.invited_by_email}
                </dd>
              </div>
            )}
            {single.expires_at && (
              <div className="flex items-baseline gap-3 min-w-0">
                <dt className="w-20 flex-shrink-0 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Expires
                </dt>
                <dd className="flex-1 min-w-0 text-sm text-gray-500 dark:text-gray-400 truncate">
                  {formatDate(single.expires_at)}
                </dd>
              </div>
            )}
          </dl>

          {error && (
            <div className="mt-5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mt-7">
            <PrimaryButton
              onClick={() => handleAccept(single)}
              disabled={actionId === single.id}
              className="w-full"
            >
              {actionId === single.id ? 'Joining…' : 'Accept invitation'}
            </PrimaryButton>
            <div className="mt-3 flex items-center justify-center gap-3 text-xs">
              <button
                onClick={() => handleDecline(single)}
                disabled={actionId === single.id}
                className="font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
              >
                Decline
              </button>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <button
                onClick={handleLater}
                className="font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Remind me later
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="px-8 pt-9 pb-3 text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Workspace invitations
            </h3>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
              You've been invited to {invitations.length} workspaces.
            </p>
          </div>

          <div className="px-6 pb-2 space-y-3">
            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <ul className="space-y-2">
              {invitations.map((inv) => (
                <li key={inv.id} className="flex items-center gap-3 px-2 py-2.5">
                  <WorkspaceTile name={inv.workspace_name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {inv.workspace_name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
                      {inv.invited_by_name && inv.invited_by_email
                        ? `${inv.invited_by_name} · ${inv.invited_by_email}`
                        : inv.invited_by_name || inv.invited_by_email || 'a workspace owner'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <PrimaryButton
                      onClick={() => handleAccept(inv)}
                      disabled={actionId === inv.id}
                      className="!px-3.5 !py-1.5 text-xs"
                    >
                      {actionId === inv.id ? '…' : 'Accept'}
                    </PrimaryButton>
                    <button
                      onClick={() => handleDecline(inv)}
                      disabled={actionId === inv.id}
                      className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                    >
                      Decline
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="px-8 pb-6 pt-3 flex justify-center">
            <button
              onClick={handleLater}
              className="text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              Remind me later
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
};

export default PendingInvitationsModal;
